import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Table } from 'react-bootstrap';
import { API_URL } from '../../const';
import axios from 'axios';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import RichTextEditor from 'react-rte';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import { Oval } from "react-loader-spinner";

const FAQ = () => {
    const [faqList, setFaqList] = useState([]);
    const [filteredList, setFilteredList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');

    const [faqForm, setFaqForm] = useState({
        id: null,
        category: "",
        question: "",
        answer: RichTextEditor.createEmptyValue(),
        status: "active"
    });

    const faqCategories = [
        "Income Tax Return",
        "GST",
        "GST Refund",
        "GST Litigation",
        "Income Tax Audit",
        "Accounting",
        "DSC",
        "Office Files",
        "Data Path",
        "ID Use",
        "Collections",
        "Others",
        "IT System"
    ];

    const getData = () => {
        setLoading(true);
        axios.post(API_URL.FAQ_LIST, {}).then(
            (res) => {
                if (res.data.status === 1) {
                    setFaqList(res.data.data);
                    setFilteredList(res.data.data);
                }
                setLoading(false);
            },
            (error) => {
                // For demonstration purposes, if API fails (e.g. not implemented yet), 
                // we'll keep the list empty or you can mock data here.
                // toast.error("Something went wrong loading FAQs");
                setLoading(false);
            }
        );
    };

    useEffect(() => {
        getData();
    }, []);

    useEffect(() => {
        let list = faqList;
        if (selectedCategory !== 'All') {
            list = list.filter(item => item.category === selectedCategory);
        }
        if (searchQuery) {
            list = list.filter(item => 
                item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.category.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }
        setFilteredList(list);
    }, [searchQuery, selectedCategory, faqList]);

    const handleOpenModal = (item = null) => {
        if (item) {
            setIsEdit(true);
            setFaqForm({
                id: item.id,
                category: item.category,
                question: item.question,
                answer: RichTextEditor.createFromString(item.answer || "", 'html'),
                status: item.status || "active"
            });
        } else {
            setIsEdit(false);
            setFaqForm({
                id: null,
                category: "",
                question: "",
                answer: RichTextEditor.createEmptyValue(),
                status: "active"
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    const handleSave = () => {
        if (!faqForm.category || !faqForm.question) {
            toast.error("Please fill all required fields");
            return;
        }

        const url = isEdit ? API_URL.FAQ_UPDATE : API_URL.FAQ_SAVE;
        const body = {
            ...faqForm,
            answer: faqForm.answer.toString('html')
        };

        axios.post(url, body).then((res) => {
            if (res.data.status === 1) {
                toast.success(isEdit ? "FAQ updated successfully" : "FAQ saved successfully");
                handleCloseModal();
                getData();
            } else {
                toast.error(res.data.message || "Failed to save FAQ");
            }
        }).catch(err => {
            toast.error("Error connecting to server");
        });
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You want to delete this FAQ?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085D6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!"
        }).then((result) => {
            if (result.isConfirmed) {
                axios.post(API_URL.FAQ_DELETE, { id }).then((res) => {
                    if (res.data.status === 1) {
                        toast.success("FAQ deleted successfully");
                        getData();
                    } else {
                        toast.error("Failed to delete FAQ");
                    }
                });
            }
        });
    };

    const toolbarConfig = {
        display: ["INLINE_STYLE_BUTTONS", "BLOCK_TYPE_BUTTONS", "HISTORY_BUTTONS"],
        INLINE_STYLE_BUTTONS: [
            { label: "Bold", style: "BOLD" },
            { label: "Italic", style: "ITALIC" },
            { label: "Underline", style: "UNDERLINE" },
        ],
        BLOCK_TYPE_BUTTONS: [
            { label: "UL", style: "unordered-list-item" },
            { label: "OL", style: "ordered-list-item" },
            { label: "H1", style: "header-one" },
            { label: "H2", style: "header-two" },
        ],
    };

    return (
        <div className="w-100">
            <div className="card shadow-sm ctm-border-radius">
                <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h4 className="title mb-0">FAQ</h4>
                        <button 
                            className="btn btn-theme ctm-border-radius text-white"
                            onClick={() => handleOpenModal()}
                        >
                            <FaPlus className="mr-1" /> Add FAQ
                        </button>
                    </div>

                    <hr />

                    <div className="row mb-3">
                        <div className="col-md-4">
                            <Form.Select 
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                                className="ctm-border-radius"
                            >
                                <option value="All">All Categories</option>
                                {faqCategories.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </Form.Select>
                        </div>
                        <div className="col-md-8">
                            <Form.Control 
                                type="text"
                                placeholder="Search FAQ by question or category..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="ctm-border-radius"
                            />
                        </div>
                    </div>

                    {loading ? (
                        <div className="text-center py-5">
                            <Oval height={60} width={60} color="#7539ff" visible={true} />
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <Table hover className="ctm-table">
                                <thead className="thead-light">
                                    <tr>
                                        <th>Category</th>
                                        <th>Question</th>
                                        <th>Status</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredList.length > 0 ? filteredList.map((item) => (
                                        <tr key={item.id}>
                                            <td><span className="badge badge-primary">{item.category}</span></td>
                                            <td>{item.question}</td>
                                            <td>
                                                <span className={`badge ${item.status === 'active' ? 'badge-success' : 'badge-danger'}`}>
                                                    {item.status === 'active' ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="table-action">
                                                    <button 
                                                        className="btn btn-sm btn-outline-success me-2"
                                                        onClick={() => handleOpenModal(item)}
                                                    >
                                                        <FaEdit />
                                                    </button>
                                                    <button 
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => handleDelete(item.id)}
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan="4" className="text-center">No FAQs found</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </div>
            </div>

            {/* Add/Edit Modal */}
            <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
                <Modal.Header closeButton className="modal-header-badges">
                    <Modal.Title>{isEdit ? "Edit FAQ" : "Add New FAQ"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>FAQ Category *</Form.Label>
                            <Form.Select 
                                value={faqForm.category}
                                onChange={(e) => setFaqForm({...faqForm, category: e.target.value})}
                                required
                            >
                                <option value="">Select Category</option>
                                {faqCategories.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Question *</Form.Label>
                            <Form.Control 
                                as="textarea"
                                rows={2}
                                value={faqForm.question}
                                onChange={(e) => setFaqForm({...faqForm, question: e.target.value})}
                                placeholder="Enter FAQ question"
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Answer *</Form.Label>
                            <RichTextEditor
                                toolbarConfig={toolbarConfig}
                                value={faqForm.answer}
                                onChange={(e) => setFaqForm({...faqForm, answer: e })}
                                placeholder="Enter FAQ answer..."
                                className="faq-rte"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="d-block">Status</Form.Label>
                            <Form.Check 
                                inline
                                type="radio"
                                label="Active"
                                name="status"
                                checked={faqForm.status === 'active'}
                                onChange={() => setFaqForm({...faqForm, status: 'active'})}
                            />
                            <Form.Check 
                                inline
                                type="radio"
                                label="Inactive"
                                name="status"
                                checked={faqForm.status === 'inactive'}
                                onChange={() => setFaqForm({...faqForm, status: 'inactive'})}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal} className="ctm-border-radius">
                        Cancel
                    </Button>
                    <Button variant="theme" onClick={handleSave} className="ctm-border-radius text-white">
                        Save
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default FAQ;
